/**
 * Conditional Logger Utility
 *
 * Provides logging that only runs in development environments.
 * In production, logs are silenced to avoid performance overhead and information leakage.
 */

const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development';

/**
 * Logger interface that mirrors console API but only logs in development
 */
export const logger = {
  /**
   * Log informational messages (development only)
   */
  log: (...args: unknown[]): void => {
    if (isDev) {
      console.log(...args);
    }
  },

  /**
   * Log informational messages (development only)
   */
  info: (...args: unknown[]): void => {
    if (isDev) {
      console.info(...args);
    }
  },

  /**
   * Log warnings (development only)
   */
  warn: (...args: unknown[]): void => {
    if (isDev) {
      console.warn(...args);
    }
  },

  /**
   * Log errors (always logs, even in production)
   * Note: Errors are important for debugging production issues
   */
  error: (...args: unknown[]): void => {
    console.error(...args);
  },

  /**
   * Log debug messages (development only)
   */
  debug: (...args: unknown[]): void => {
    if (isDev) {
      console.debug(...args);
    }
  },
};

/**
 * Helper to check if we're in development mode
 */
export const isDevMode = (): boolean => isDev;
